/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {CompanyContact} from 'types/entities/CompanyContact'
import {ContactCompany} from 'types/entities/ContactCompany'
import {Company} from 'types/entities/Company'
import {ThunkAction} from 'model/store/thunk'
import {
	pushContactCompany,
	removeContactCompany,
	setContacts,
	setContactsTableMeta,
	setSelectedContact,
	setSelectedContactCompanies,
	setSelectedContactLeads,
	setSelectedContactOffers,
} from './ContactsReducer'
import {parsePaginatedResponse} from 'utils/api/pagination'
import RequestAction from 'store/RequestAction'
import { CsvImportMap } from 'components/XlsxImport/types'
import { XlsxImportResult } from 'types/import/XlsxImportResult'
import { parseImportResponse } from 'utils/import/parseImportResponse'
import {ContactsFilterType} from 'types/enums/ContactsFilterType'
import {MassActionRequest, MassActionResponse} from 'types/requests/MassActionRequest'

export class ContactsAction {
    static fetchContactsTablePage = (params: {
        page: number
        per_page: number
        type?: ContactsFilterType
        search?: any
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
    }): ThunkAction<
        Promise<{
            data: CompanyContact[]
            current_page: number
            per_page: number
            total: number
        }>
    > => {
        return async (
            dispatch,
            getState
        ): Promise<{
            data: CompanyContact[]
            current_page: number
            per_page: number
            total: number
        }> => {
            const url = `/admin/contact_companies/table`

            const hasClientSorting = params.sorting && params.sorting.length > 0

            const effectiveSorting = hasClientSorting
                ? params.sorting
                : [{ id: 'id', desc: true }]

            const response: unknown =
                await RequestAction.makeAuthenticatedRequest(
                    getState,
                    url,
                    {
                        page: params.page,
                        per_page: params.per_page,
                        type:
                            params.type && params.type !== 'all'
                                ? params.type
                                : undefined,
                        search: params.search,
                        sorting: effectiveSorting,
                    },
                    'post'
                )
            try {
                const parsedResponse = parsePaginatedResponse<CompanyContact>(
                    response,
                    'companyContacts'
                )

                dispatch(setContacts(parsedResponse.data))

                const sortByParam =
                    params.sort_by ??
                    (Array.isArray(effectiveSorting) &&
                    effectiveSorting.length > 0
                        ? effectiveSorting[0].id
                        : null)

                const sortParam =
                    params.sort ??
                    (Array.isArray(effectiveSorting) &&
                    effectiveSorting.length > 0
                        ? effectiveSorting[0].desc
                            ? 'desc'
                            : 'asc'
                        : null)

                dispatch(
                    setContactsTableMeta({
                        currentPage: parsedResponse.current_page,
                        perPage: parsedResponse.per_page,
                        total: parsedResponse.total,
                        sortBy: sortByParam,
                        sort: sortParam,
                        search: params.search,
                    })
                )

                return parsedResponse
            } catch {
                return Promise.reject(response)
            }
        }
    }

    static fetchContactByIdRaw = (contactId: number): ThunkAction<Promise<CompanyContact>> => {
        return async (_dispatch, getState) => {
            const response = await RequestAction.makeAuthenticatedRequest<{companyContact?: CompanyContact}>(
                getState,
                `/admin/contact_companies/detail/${contactId}`,
                undefined,
                'get'
            )
            if (response?.companyContact) return response.companyContact
            return Promise.reject(response)
        }
    }

    static fetchContactById = (
        contactId: number
    ): ThunkAction<Promise<CompanyContact>> => {
        return async (dispatch, getState): Promise<CompanyContact> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/contact_companies/detail/${contactId}`,
                undefined,
                'get'
            )

            const contact: any = response?.companyContact
            const companies: any[] | undefined = contact?.companies
            const leads: any[] | undefined = contact?.leads
            const offers: any[] | undefined = contact?.offers

            if (contact) {
                // Remove companies from contact object before setting it to selectedContact
                const contactData = { ...contact }
                delete contactData.companies
                delete contactData.leads
                delete contactData.offers
                dispatch(setSelectedContact(contactData))

                const formattedCompanies: ContactCompany[] = (companies ?? []).map(
                    (c: any) => ({
                        company_id: c.id,
                        user_id: contact.id,
                        company: c,
                    })
                )
                dispatch(setSelectedContactCompanies(formattedCompanies))
                dispatch(setSelectedContactLeads(leads ?? []))
                dispatch(setSelectedContactOffers(offers ?? []))
                return contact
            }
            return Promise.reject(response)
        }
    }

    static fetchAssignToCompany = (
        contactId: number,
        company: Company
    ): ThunkAction<Promise<ContactCompany>> => {
        return async (dispatch, getState): Promise<ContactCompany> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/contact_companies/assign/${contactId}`,
                {
                    company_id: company.id,
                },
                'post'
            )

            const contactCompanyResponse = response?.contactCompany ?? response

            if (contactCompanyResponse) {
                const contactCompany: ContactCompany = {
                    ...contactCompanyResponse,
                    company_id: company.id,
                    user_id: contactId,
                    company: company,
                }
                dispatch(pushContactCompany(contactCompany))
                return contactCompany
            }
            return Promise.reject(response)
        }
    }

    static fetchMassAction = (
        payload: MassActionRequest
    ): ThunkAction<Promise<MassActionResponse>> => {
        return async (_dispatch, getState): Promise<MassActionResponse> => {
            return await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/contact_companies/mass-actions',
                payload,
                'post'
            )
        }
    }

    static fetchUnassignFromCompany = (
        contactId: number,
        companyId: number
    ): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/contact_companies/unassign/${contactId}`,
                {
                    company_id: companyId,
                },
                'delete'
            )

            const success = response?.success ?? response

            if (success) {
                dispatch(removeContactCompany({ companyId }))
                return Promise.resolve()
            }
            return Promise.reject(response)
        }
    }

    static fetchDeleteContact = (
        contactId: number
    ): ThunkAction<Promise<void>> => {
        return async (_dispatch, getState): Promise<void> => {
            const response: {
                success: boolean
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/contact_companies/delete/${contactId}`,
                undefined,
                'delete'
            )

            if (response?.success) {
                // dispatch(removeContact(contactId)) // Ideally we'd remove from contacts list if present, but for now just resolving is enough as the caller will handle UI updates
                return Promise.resolve()
            }
            return Promise.reject(response)
        }
    }
    static setSelectedContact = (contact: CompanyContact | null) => {
        return setSelectedContact(contact)
    }

    static resetSelectedContact = (): ThunkAction<void> => {
        return (dispatch) => {
            dispatch(setSelectedContact(null))
            dispatch(setSelectedContactCompanies([]))
            dispatch(setSelectedContactLeads([]))
            dispatch(setSelectedContactOffers([]))
        }
    }

    static fetchExportContactsXlsx = (params: {
        page?: number
        per_page?: number
        type?: ContactsFilterType
        search?: any
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
        export?: 'xlsx' | 'csv' | 'pdf'
        [key: string]: any
    }): ThunkAction<Promise<Blob | string>> => {
        return async (_dispatch, getState): Promise<Blob | string> => {
            const url = `/admin/export/contacts`

            const hasClientSorting = params.sorting && params.sorting.length > 0

            const effectiveSorting = hasClientSorting
                ? params.sorting
                : [{ id: 'id', desc: true }]

            const response: unknown = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    ...params,
                    page: params.page ?? 1,
                    per_page: params.per_page ?? 50,
                    type:
                        params.type && params.type !== 'all'
                            ? params.type
                            : undefined,
                    search: params.search,
                    sorting: effectiveSorting,
                    export: params.export ?? 'xlsx',
                },
                'post',
                'blob'
            )

            if (response instanceof Blob) {
                return response
            }

            if (typeof response === 'string' && response) {
                return response
            }

            if (response && typeof response === 'object') {
                const downloadUrl =
                    (response as { url?: unknown; file?: unknown }).url ??
                    (response as { url?: unknown; file?: unknown }).file
                if (typeof downloadUrl === 'string' && downloadUrl) {
                    return downloadUrl
                }
            }

            return Promise.reject(response)
        }
    }
    static fetchImportContacts = (payload: CsvImportMap): ThunkAction<Promise<XlsxImportResult>> => {
        return async (_dispatch, getState): Promise<XlsxImportResult> => {
            const response: unknown = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/contact_companies/import',
                {
                    file: payload.file,
                    fileLocation: payload.fileLocation,
                    columns: payload.columns,
                    importOptions: payload.importOptions,
                    duplicateStrategy: payload.duplicateStrategy,
                },
                'post'
            )

            const parsed = parseImportResponse(response)
            if (parsed.success === false && parsed.errors?.length) {
                return Promise.reject(parsed)
            }

            return parsed
        }
    }
}
